import React, { useEffect, useState, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, Image as ImageIcon, X, ChevronDown, ChevronRight, FolderOpen, Grid3X3, List } from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend base; strip trailing slashes and an accidental "/api" suffix to avoid double-prefixing
const rawBackend = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const BACKEND_BASE = rawBackend.replace(/\/+$|\/api$/i, '');
const API = `${BACKEND_BASE}/api`;

const getWebSocketUrl = () => {
  // BACKEND_BASE is http(s)://...; convert to ws(s)://...
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const CATEGORY_OPTIONS = ['Facilities', 'Events', 'Community', 'Training', 'Highlights', 'Coaches', 'Players', 'Courts', 'Amenities', 'General'];

// Category colors for visual distinction
const CATEGORY_COLORS = {
  'Facilities': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30', accent: 'bg-blue-500' },
  'Events': { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30', accent: 'bg-purple-500' },
  'Community': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', accent: 'bg-green-500' },
  'Training': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30', accent: 'bg-orange-500' },
  'Highlights': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30', accent: 'bg-yellow-500' },
  'Coaches': { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30', accent: 'bg-red-500' },
  'Players': { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30', accent: 'bg-pink-500' },
  'Courts': { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30', accent: 'bg-cyan-500' },
  'Amenities': { bg: 'bg-indigo-500/20', text: 'text-indigo-300', border: 'border-indigo-500/30', accent: 'bg-indigo-500' },
  'General': { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-500/30', accent: 'bg-slate-500' },
};

// If production Nginx has a small client_max_body_size (often 1MB), compress images below that threshold
const TARGET_UPLOAD_MAX_BYTES = 700 * 1024; // ~700KB (conservative for 1MB Nginx limit)
const TARGET_IMAGE_MAX_DIMENSION = 1600;

const loadImageElement = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = (err) => {
    URL.revokeObjectURL(url);
    reject(err);
  };
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), type, quality);
});

const compressImageIfNeeded = async (file) => {
  // Keep gifs as-is (canvas conversion breaks animation).
  if (!file || file.type === 'image/gif') return { file, changed: false };
  if (file.size <= TARGET_UPLOAD_MAX_BYTES) return { file, changed: false };

  try {
    const img = await loadImageElement(file);
    const srcWidth = img.naturalWidth || img.width;
    const srcHeight = img.naturalHeight || img.height;
    if (!srcWidth || !srcHeight) return { file, changed: false };

    const scale = Math.min(1, TARGET_IMAGE_MAX_DIMENSION / Math.max(srcWidth, srcHeight));
    const outWidth = Math.max(1, Math.round(srcWidth * scale));
    const outHeight = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { file, changed: false };

    // White background to avoid black fill when converting transparent PNG -> JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outWidth, outHeight);
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    // Iteratively lower JPEG quality until we hit target size.
    const outType = 'image/jpeg';
    let quality = 0.80;
    let blob = await canvasToBlob(canvas, outType, quality);
    while (blob && blob.size > TARGET_UPLOAD_MAX_BYTES && quality > 0.3) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, outType, quality);
    }

    if (!blob) return { file, changed: false };
    if (blob.size > file.size) return { file, changed: false };

    const baseName = (file.name || 'upload').replace(/\.[^/.]+$/, '');
    const newFile = new File([blob], `${baseName}.jpg`, { type: outType });
    return { file: newFile, changed: true };
  } catch {
    return { file, changed: false };
  }
};

const AdminGalleryPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // View mode: 'categories' (sections) or 'grid' (all items)
  const [viewMode, setViewMode] = useState('categories');
  
  // Active category filter (for grid view)
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Expanded categories (for category view)
  const [expandedCategories, setExpandedCategories] = useState(
    CATEGORY_OPTIONS.reduce((acc, cat) => ({ ...acc, [cat]: true }), {})
  );

  // Synchronous guard to prevent double-submit before React state updates.
  const submitGuardRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    category: '',
    description: '',
    image: '',
    file: null,
  });
  const [errors, setErrors] = useState({ title: '', category: '', image: '' });
  const [touched, setTouched] = useState({ title: false, category: false, image: false });
  const [formAlert, setFormAlert] = useState({ type: '', message: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API}/admin/gallery`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to load gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  // Realtime updates via WebSocket (auto-reconnect).
  useEffect(() => {
    if (!token) return;

    let ws;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      ws = new WebSocket(getWebSocketUrl());

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (!msg || typeof msg !== 'object') return;

          if (msg.type === 'gallery_created' && msg.item?.gallery_id) {
            const created = msg.item;
            setItems((prev) => {
              if (prev.some((it) => it.gallery_id === created.gallery_id)) return prev;
              return [created, ...prev];
            });
          }

          if (msg.type === 'gallery_deleted' && msg.gallery_id) {
            const id = msg.gallery_id;
            setItems((prev) => prev.filter((it) => it.gallery_id !== id));
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onopen = () => {
        reconnectDelayMs = 2000;
      };

      ws.onclose = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
          connect();
        }, reconnectDelayMs);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [token]);

  // Group items by category
  const itemsByCategory = CATEGORY_OPTIONS.reduce((acc, cat) => {
    acc[cat] = items.filter(item => item.category === cat);
    return acc;
  }, {});

  // Get category counts
  const categoryCounts = CATEGORY_OPTIONS.reduce((acc, cat) => {
    acc[cat] = itemsByCategory[cat].length;
    return acc;
  }, {});

  // Filter items for grid view
  const filteredItems = activeCategory === 'all' 
    ? items 
    : items.filter(item => item.category === activeCategory);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const expandAll = () => {
    setExpandedCategories(CATEGORY_OPTIONS.reduce((acc, cat) => ({ ...acc, [cat]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedCategories(CATEGORY_OPTIONS.reduce((acc, cat) => ({ ...acc, [cat]: false }), {}));
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'title':
        return value.trim() ? '' : 'Title is required';
      case 'category':
        return value ? '' : 'Category is required';
      case 'image':
        return newItem.file || newItem.image ? '' : 'Image is required';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, newItem[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleInputChange = (field, value) => {
    setNewItem((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (file.size > 14 * 1024 * 1024) {
      const msg = 'Image size must be less than 14MB';
      setErrors((prev) => ({ ...prev, image: msg }));
      setFormAlert({ type: 'error', message: msg });
      setTouched((prev) => ({ ...prev, image: true }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      const msg = 'Invalid file type. Allowed: JPG, PNG, GIF, WEBP';
      setErrors((prev) => ({ ...prev, image: msg }));
      setFormAlert({ type: 'error', message: msg });
      setTouched((prev) => ({ ...prev, image: true }));
      if (e?.target) e.target.value = '';
      return;
    }

    setErrors((prev) => ({ ...prev, image: '' }));
    setFormAlert({ type: '', message: '' });

    const { file: uploadFile, changed } = await compressImageIfNeeded(file);
    const sizeMB = (uploadFile.size / (1024 * 1024)).toFixed(2);
    
    if (changed) {
      toast.success(`Image optimized to ${sizeMB}MB for upload`);
    } else if (uploadFile.size > TARGET_UPLOAD_MAX_BYTES) {
      toast.warning(`Image is ${sizeMB}MB. Server may reject uploads >1MB.`);
    }

    const previewUrl = URL.createObjectURL(uploadFile);
    setNewItem((prev) => ({ ...prev, image: previewUrl, file: uploadFile }));
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    const titleError = validateField('title', newItem.title);
    const categoryError = validateField('category', newItem.category);
    const imageError = validateField('image', newItem.image);

    setErrors({ title: titleError, category: categoryError, image: imageError });
    setTouched({ title: true, category: true, image: true });

    if (titleError || categoryError || imageError || saving) return;
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    try {
      setSaving(true);
      setFormAlert({ type: '', message: '' });

      const formData = new FormData();
      formData.append('title', newItem.title);
      formData.append('category', newItem.category);
      formData.append('description', newItem.description || '');
      if (newItem.file) {
        formData.append('image', newItem.file);
      }

      const response = await fetch(`${API}/admin/gallery`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Idempotency-Key': idempotencyKeyRef.current,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 405) {
          throw new Error('Backend does not allow POST /api/admin/gallery (405). Restart the backend so the latest routes load.');
        }
        let errorMessage = `Failed to save gallery item (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
        if (response.status === 413) {
          errorMessage = 'Upload rejected (413). The server/proxy is limiting upload size.';
        }
        const contentType = response.headers.get('content-type') || '';
        try {
          if (contentType.includes('application/json')) {
            const body = await response.json();
            if (body?.detail) {
              if (typeof body.detail === 'string') errorMessage = body.detail;
              else if (Array.isArray(body.detail)) errorMessage = body.detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join('\n') || errorMessage;
            } else if (body?.error) {
              errorMessage = body.error;
            }
          } else {
            const text = await response.text();
            if (text && text.trim()) {
              errorMessage = response.status === 413
                ? `Upload rejected (413). The server/proxy is limiting upload size. ${text.trim()}`
                : text.trim();
            }
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(errorMessage);
      }

      await response.json();
      toast.success('Gallery item saved');
      setShowAddModal(false);
      setNewItem({ title: '', category: '', description: '', image: '', file: null });
      setErrors({ title: '', category: '', image: '' });
      setTouched({ title: false, category: false, image: false });
      setFormAlert({ type: '', message: '' });
      // Refresh list to ensure a single entry even if a transient duplicate render occurred.
      fetchGallery();
    } catch (error) {
      const msg = error?.message || 'Failed to save gallery item. Please try again.';
      console.error('Error saving gallery item:', error);
      setFormAlert({ type: 'error', message: msg });
      toast.error(msg);
    } finally {
      setSaving(false);
      submitGuardRef.current = false;
      idempotencyKeyRef.current = null;
    }
  };

  const deleteItem = async (galleryId) => {
    if (!confirm('Delete this gallery item?')) return;
    try {
      const res = await fetch(`${API}/admin/gallery/${galleryId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        throw new Error('Failed to delete');
      }
      toast.success('Deleted');
      fetchGallery();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setNewItem({ title: '', category: '', description: '', image: '', file: null });
    setErrors({ title: '', category: '', image: '' });
    setTouched({ title: false, category: false, image: false });
    setFormAlert({ type: '', message: '' });
  };

  const openAddModalWithCategory = (category) => {
    resetForm();
    setNewItem(prev => ({ ...prev, category }));
    setShowAddModal(true);
  };

  const renderEmpty = () => (
    <div className={`text-center py-16 border-2 border-dashed rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-800/30' : 'border-gray-300 bg-gray-50'}`}>
      <ImageIcon className={`h-10 w-10 mx-auto mb-3 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
      <p className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>No gallery items yet</p>
      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>Add images to showcase your venues, events, and community.</p>
      {hasRole(['super_admin', 'manager']) && (
        <Button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-emerald-600 mt-4 text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Gallery Item
        </Button>
      )}
    </div>
  );

  const renderGalleryCard = (item) => {
    const colors = CATEGORY_COLORS[item.category] || CATEGORY_COLORS['General'];
    return (
      <Card key={item.gallery_id} className={`transition-all hover:shadow-lg group ${isDarkMode ? 'bg-slate-800/50 border-slate-700 hover:border-slate-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
        <CardContent className="p-3 space-y-2">
          <div className={`aspect-square rounded-lg flex items-center justify-center overflow-hidden relative ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
            {item.image ? (
              <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            ) : (
              <ImageIcon className="h-12 w-12 text-slate-500" />
            )}
            {hasRole(['super_admin', 'manager']) && (
              <Button 
                size="sm" 
                variant="ghost" 
                className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                onClick={() => deleteItem(item.gallery_id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div>
            <h3 className={`font-medium text-sm line-clamp-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
            {item.description && <p className={`text-xs line-clamp-1 mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{item.description}</p>}
          </div>
          <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
            <span className={`px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} capitalize text-[10px]`}>{item.category}</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCategorySection = (category) => {
    const categoryItems = itemsByCategory[category];
    const isExpanded = expandedCategories[category];
    const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['General'];
    const count = categoryItems.length;

    return (
      <div key={category} className={`border ${colors.border} rounded-xl overflow-hidden ${isDarkMode ? 'bg-slate-800/30' : 'bg-white'}`}>
        {/* Category Header */}
        <button
          onClick={() => toggleCategory(category)}
          className={`w-full flex items-center justify-between px-4 py-3 ${colors.bg} hover:brightness-110 transition-all`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-1 h-6 rounded-full ${colors.accent}`} />
            <FolderOpen className={`h-5 w-5 ${colors.text}`} />
            <span className={`font-semibold ${colors.text}`}>{category}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full bg-slate-900/50 ${colors.text}`}>
              {count} {count === 1 ? 'image' : 'images'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasRole(['super_admin', 'manager']) && (
              <Button
                size="sm"
                variant="ghost"
                className={`${colors.text} hover:bg-white/10 h-8 px-2`}
                onClick={(e) => { e.stopPropagation(); openAddModalWithCategory(category); }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            )}
            {isExpanded ? (
              <ChevronDown className={`h-5 w-5 ${colors.text}`} />
            ) : (
              <ChevronRight className={`h-5 w-5 ${colors.text}`} />
            )}
          </div>
        </button>

        {/* Category Content */}
        {isExpanded && (
          <div className="p-4">
            {count === 0 ? (
              <div className={`text-center py-8 border-2 border-dashed rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-300'}`}>
                <ImageIcon className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>No images in this category</p>
                {hasRole(['super_admin', 'manager']) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`mt-2 ${colors.text}`}
                    onClick={() => openAddModalWithCategory(category)}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Image
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {categoryItems.map(renderGalleryCard)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gallery Management</h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Upload and manage images shown on the public site.</p>
          </div>
          <div className="flex items-center gap-2">
            {hasRole(['super_admin', 'manager']) && (
              <Button
                onClick={() => { resetForm(); setShowAddModal(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Image
              </Button>
            )}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-2">
          {CATEGORY_OPTIONS.map(cat => {
            const colors = CATEGORY_COLORS[cat];
            const count = categoryCounts[cat];
            return (
              <button
                key={cat}
                onClick={() => {
                  if (viewMode === 'grid') {
                    setActiveCategory(activeCategory === cat ? 'all' : cat);
                  } else {
                    // Scroll to category section
                    setExpandedCategories(prev => ({ ...prev, [cat]: true }));
                    document.getElementById(`category-${cat}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className={`px-3 py-2 rounded-lg ${colors.bg} ${colors.border} border hover:brightness-110 transition-all ${activeCategory === cat && viewMode === 'grid' ? 'ring-2 ring-white/50' : ''}`}
              >
                <div className={`text-lg font-bold ${colors.text}`}>{count}</div>
                <div className={`text-xs ${colors.text} opacity-80 truncate`}>{cat}</div>
              </button>
            );
          })}
        </div>

        {/* View Toggle & Controls */}
        <div className={`flex items-center justify-between rounded-lg p-3 border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm mr-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>View:</span>
            <Button
              size="sm"
              variant={viewMode === 'categories' ? 'default' : 'ghost'}
              className={viewMode === 'categories' ? 'bg-emerald-600' : isDarkMode ? 'text-slate-400' : 'text-gray-600'}
              onClick={() => setViewMode('categories')}
            >
              <List className="h-4 w-4 mr-1" /> Categories
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              className={viewMode === 'grid' ? 'bg-emerald-600' : isDarkMode ? 'text-slate-400' : 'text-gray-600'}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4 mr-1" /> Grid
            </Button>
          </div>
          
          {viewMode === 'categories' && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} onClick={expandAll}>
                Expand All
              </Button>
              <Button size="sm" variant="ghost" className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} onClick={collapseAll}>
                Collapse All
              </Button>
            </div>
          )}

          {viewMode === 'grid' && (
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Filter:</span>
              <select
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
                className={`text-sm rounded-lg px-3 py-1.5 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="all">All Categories ({items.length})</option>
                {CATEGORY_OPTIONS.map(cat => (
                  <option key={cat} value={cat}>{cat} ({categoryCounts[cat]})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        ) : items.length === 0 ? (
          renderEmpty()
        ) : viewMode === 'categories' ? (
          <div className="space-y-4">
            {CATEGORY_OPTIONS.map(cat => (
              <div key={cat} id={`category-${cat}`}>
                {renderCategorySection(cat)}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredItems.map(renderGalleryCard)}
          </div>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className={`w-full max-w-lg rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-black'}`}>
              <div className={`flex items-center justify-between border-b px-5 py-4 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Add Gallery Image</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className={`rounded-full p-1 ${isDarkMode ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
                  aria-label="Close add gallery modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleAddItem} className="px-5 pb-5 pt-4 space-y-4">
                {formAlert.message && (
                  <div className={`rounded-lg border px-4 py-3 text-sm flex gap-2 ${formAlert.type === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`}>
                    <span className="font-semibold">{formAlert.type === 'error' ? 'Error' : 'Success'}:</span>
                    <span>{formAlert.message}</span>
                  </div>
                )}

                {/* Category Selection - Visual grid for better UX */}
                <div>
                  <label className={`mb-2 block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Category <span className="text-red-600">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CATEGORY_OPTIONS.map((cat) => {
                      const colors = CATEGORY_COLORS[cat];
                      const isSelected = newItem.category === cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleInputChange('category', cat)}
                          className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            isSelected 
                              ? isDarkMode ? 'border-emerald-500 bg-emerald-900/30' : 'border-emerald-500 bg-emerald-50'
                              : isDarkMode ? 'border-slate-600 hover:border-slate-500 bg-slate-700' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                          }`}
                        >
                          <span className={isSelected ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}>
                            {cat}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {touched.category && errors.category && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.category}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Image <span className="text-red-600">*</span>
                  </label>
                  <div className="flex items-start gap-4">
                    <div className={`h-32 w-32 overflow-hidden rounded-xl border-2 border-dashed ${touched.image && errors.image ? 'border-red-500' : isDarkMode ? 'border-slate-600' : 'border-gray-300'} ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'} flex items-center justify-center flex-shrink-0 transition-all hover:border-emerald-500`}>
                      {newItem.image ? (
                        <img src={newItem.image} alt="Preview" className="h-full w-full object-cover" />
                      ) : (
                        <div className="text-center p-2">
                          <ImageIcon className={`h-8 w-8 mx-auto mb-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                          <span className={`text-xs block ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Preview</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        onBlur={() => handleBlur('image')}
                        required
                        className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-600 hover:file:bg-emerald-100 file:cursor-pointer"
                      />
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Max size: 14MB. Formats: JPG, PNG, GIF, WEBP</p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Images will be auto-compressed for optimal upload.</p>
                      {touched.image && errors.image && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <span>⚠</span> {errors.image}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Title <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    onBlur={() => handleBlur('title')}
                    className={`w-full rounded-md border ${touched.title && errors.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : isDarkMode ? 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500' : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'} px-3 py-2 text-sm ${isDarkMode ? 'text-white bg-slate-700' : 'text-gray-900 bg-white'} focus:outline-none focus:ring-2 transition-colors`}
                    placeholder="e.g., Sunset courts, Clubhouse, Tournament finals"
                    required
                  />
                  {touched.title && errors.title && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.title}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, description: e.target.value }))}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${isDarkMode ? 'border-slate-600 text-white bg-slate-700' : 'border-gray-300 text-gray-900 bg-white'}`}
                    placeholder="Optional caption or context"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className={isDarkMode ? 'border-slate-600 text-slate-300' : 'border-gray-300 text-gray-700'}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      saving ||
                      (touched.title && errors.title) ||
                      (touched.category && errors.category) ||
                      (touched.image && errors.image)
                    }
                    className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Image'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGalleryPage;
